<template>
    <section
        :class="[$style['mds-popover'], classObject, positionClass, widthClass]"
        role="tooltip"
        :aria-hidden="isVisible ? 'false' : 'true'"
        :triggered-by="triggeredBy"
        :data-mds-version="version"
    >
        <mds-section
            :border="headerBorder"
            border-position="bottom"
            :heading-level="headingLevel"
            :title="title"
            :size="headerSize"
            bold
        >
            <template v-slot:mds-section-actions>
                <slot name="mds-popover-actions"></slot>
            </template>
            <slot></slot>
        </mds-section>
    </section>
</template>

<script>
    import { MdsPositioning, MdsTrapFocus } from '@mds/utils-js';
    import MdsSection from '@mds/section';
    import MdsPopoverProps from './popover_props.js';

    export default {
        name: 'MdsPopover',
        props: MdsPopoverProps,
        mixins: [MdsPositioning, MdsTrapFocus],
        components: {
            MdsSection,
        },
        data() {
            return {
                isVisible: false,
                currentPosition: this.position,
                headerSize: 8,
                triggerElement: null,
            };
        },
        model: {
            prop: 'visible',
            event: 'mds-popover-dismissed',
        },
        watch: {
            visible(visible) {
                this[visible ? 'show' : 'hide']();
            },
        },
        computed: {
            classObject() {
                const obj = {};
                obj[this.$style['mds-popover--visible']] = this.isVisible;
                obj[this.$style['mds-popover--title-hidden']] = this.titleHidden;
                obj[this.$style['mds-popover--fixed']] = this.fixed;

                return obj;
            },
            positionClass() {
                return this.$style[`mds-popover--${this.currentPosition}`];
            },
            widthClass() {
                return this.$style[`mds-popover--width-${this.width}`];
            },
            headerBorder() {
                return this.titleHidden ? 'none' : 'primary';
            },
        },
        methods: {
            updatePosition() {
                return new Promise(resolve => {
                    const { setPosition, attributes } = this.getPositionAttributes(this.position, this.autoPosition);
                    this.currentPosition = setPosition;
                    this.$el.setAttribute('style', this.formatPositionStyleAttributes(attributes));

                    this.$emit('mds-popover-position-updated');

                    resolve();
                }).catch(error => error);
            },
            show(event) {
                this.enableTrapFocus();
                this.$nextTick(function() {
                    this.updatePosition().then(() => {
                        this.isVisible = true;
                        this.$emit('mds-popover-opened', event);
                    });
                });
            },
            hide(event) {
                // to negate duplicate dismiss event emit
                if (!this.isVisible) {
                    return;
                }
                this.isVisible = false;
                this.disableTrapFocus();

                this.$emit('mds-popover-dismissed', this.isVisible, event);
            },
            bindEventHandlers() {
                if (this.triggerElement) {
                    window.addEventListener('keydown', this.keyboardEventHandler, true);
                    window.addEventListener('click', this.bodyEventHandler, true);
                    window.addEventListener('resize', this.debounce(this.updatePosition, 100));
                    if (this.autoPosition) {
                        window.addEventListener('scroll', this.debounce(this.updatePosition, 100));
                    }
                }
            },
            keyboardEventHandler(event) {
                // Keyboard check if key is Esc
                if (event.keyCode === 27) {
                    this.hide();
                }
            },
            bodyEventHandler(event) {
                const popover = this.$el;
                let targetElement = event.target;

                // Suppress dismissal of popover when the popover is clicked
                do {
                    if (targetElement === popover || targetElement === this.triggerElement) {
                        return;
                    }
                    targetElement = targetElement.parentNode;
                } while (targetElement);

                this.hide();
            },
            removeEventHandlers() {
                if (this.triggerElement) {
                    window.removeEventListener('keydown', this.keyboardEventHandler, true);
                    window.removeEventListener('click', this.bodyEventHandler, true);
                    window.removeEventListener('resize', this.updatePosition, true);

                    if (this.autoPosition) {
                        window.removeEventListener('scroll', this.updatePosition, true);
                    }
                    this.$emit('mds-popover-listeners-destroyed');
                }
            },
        },
        mounted() {
            document.body.appendChild(this.$el);
            this.triggerElement = document.getElementById(`${this.triggeredBy}`);
            this.bindEventHandlers();
            this.$nextTick(function() {
                this.updatePosition().then(() => (this.isVisible = this.visible));
            });
        },
        beforeDestroy() {
            this.removeEventHandlers();
            if (this.$el.parentNode) {
                this.$el.parentNode.removeChild(this.$el);
            }
        },
    };
</script>

<style lang="scss" module>
    @import './popover.scss';
</style>
